// @ts-check
// Histórico de execuções (contrato 08, seção 7): situação, registro, passos e erro de cada execução, com filtro
// de falhas e de esperas, "tentar de novo" e "parar" para administradores. Equivale à lista de workflowRuns do
// Twenty, sem o diagrama.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { listAutomationRuns, retryAutomationRun, stopAutomationRun } from '../../data/automations.js';
import { Link } from '../../app/router.js';
import { Button } from '../../ui/button.js';
import { Tag } from '../../ui/chip.js';
import { Icon } from '../../ui/icon.js';
import { Skeleton } from '../../ui/feedback.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { formatDateTime, formatRelativeToNow } from '../../lib/format.js';
import { SegmentedControl } from '../dashboards/editor-controls.js';
import { Modal } from '../../ui/modal.js';
import { ACTION_ICONS, RUN_STATUS_COLORS, STEP_STATUS_COLORS, STOPPABLE_RUN_STATUSES } from './automation-labels.js';
import { t } from '../../i18n/index.js';

/** @typedef {import('../../data/automations.js').AutomationRun} AutomationRun */

const PAGE_SIZE = 25;

/**
 * @param {{ run: AutomationRun, showAutomationName: boolean, isAdmin: boolean, onRetried: () => void }} props
 */
function RunRow({ run, showAutomationName, isAdmin, onRetried }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isConfirmingStop, setIsConfirmingStop] = useState(false);
  const stop = async () => {
    setIsConfirmingStop(false);
    try {
      await stopAutomationRun(run.id);
      showSuccessSnackbar(t('automations.runs.stopped'));
      onRetried();
    } catch (error) {
      showErrorSnackbar(error);
    }
  };
  const retry = async () => {
    setIsRetrying(true);
    try {
      await retryAutomationRun(run.id);
      showSuccessSnackbar(t('automations.runs.retried'));
      onRetried();
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsRetrying(false);
    }
  };
  const recordHref = run.recordId ? `/object/${run.objectNameSingular}/${run.recordId}` : null;
  return html`<div class="automation-run" data-expanded=${isExpanded ? 'true' : undefined}>
    <button type="button" class="automation-run-summary" aria-expanded=${isExpanded ? 'true' : 'false'} onClick=${() => setIsExpanded(!isExpanded)}>
      <${Icon} name="IconChevronRight" size=${14} className="automation-run-chevron" />
      <${Tag} color=${RUN_STATUS_COLORS[/** @type {keyof typeof RUN_STATUS_COLORS} */ (run.status)] ?? 'gray'} text=${t(`automations.runStatus.${run.status}`)} />
      <span class="automation-run-title">
        ${showAutomationName ? html`<span class="automation-run-automation">${run.automationName ?? t('automations.runs.removedAutomation')}</span>` : null}
        <span class="automation-run-record">${run.recordLabel || (run.recordId ? t('record.untitled') : t(`automations.runs.withoutRecord.${run.triggerType === 'WEBHOOK' ? 'WEBHOOK' : 'SCHEDULED'}`))}</span>
      </span>
      ${run.status === 'WAITING' && run.resumeAt ? html`<span class="automation-run-resume">${t('automations.runs.resumesAt', { date: formatDateTime(run.resumeAt) })}</span>` : null}
      <span class="automation-run-time" title=${formatDateTime(run.createdAt)}>${formatRelativeToNow(run.createdAt)}</span>
    </button>
    ${isExpanded
      ? html`<div class="automation-run-details">
          ${run.error ? html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${run.error}</span></div>` : null}
          <ol class="automation-run-steps">
            ${run.steps.length === 0
              ? html`<li class="automation-run-step-empty">${t('automations.runs.noSteps')}</li>`
              : run.steps.map((step) => html`<li key=${step.actionId} class="automation-run-step">
                  <${Icon} name=${ACTION_ICONS[/** @type {keyof typeof ACTION_ICONS} */ (step.type)] ?? 'IconSettingsAutomation'} size=${14} />
                  <span class="automation-run-step-type">${t(`automations.actionTypes.${step.type}`)}</span>
                  <${Tag} color=${STEP_STATUS_COLORS[/** @type {keyof typeof STEP_STATUS_COLORS} */ (step.status)] ?? 'gray'} text=${t(`automations.stepStatus.${step.status}`)} />
                  ${step.status === 'WAITING' && step.output?.resumeAt ? html`<span class="automation-muted">${t('automations.runs.resumesAt', { date: formatDateTime(step.output.resumeAt) })}</span>` : null}
                  ${step.type === 'IF_ELSE' && step.output?.branch ? html`<span class="automation-muted">${t(`automations.ifElse.${step.output.branch}Title`)}</span>` : null}
                  ${step.status === 'SKIPPED' && step.output?.branchSkipped ? html`<span class="automation-muted">${t('automations.runs.otherBranch')}</span>` : null}
                  ${step.type === 'HTTP_REQUEST' && step.output?.status ? html`<span class="automation-muted">HTTP ${step.output.status}</span>` : null}
                  ${step.error ? html`<span class="automation-run-step-error">${step.error}</span>` : null}
                </li>`)}
          </ol>
          <div class="automation-run-meta">
            <span>${t('automations.runs.createdAt', { date: formatDateTime(run.createdAt) })}</span>
            ${run.endedAt ? html`<span>${t('automations.runs.endedAt', { date: formatDateTime(run.endedAt) })}</span>` : null}
            ${run.attempts > 1 ? html`<span>${t('automations.runs.attempts', { count: run.attempts })}</span>` : null}
            ${run.triggeredBy ? html`<span>${t('automations.runs.triggeredBy', { name: run.triggeredBy })}</span>` : null}
            ${run.versionNumber ? html`<span>${t('automations.runs.version', { number: run.versionNumber })}</span>` : null}
            ${recordHref ? html`<${Link} href=${recordHref} className="automation-run-link">${t('automations.runs.openRecord')}<//>` : null}
          </div>
          ${isAdmin && run.status === 'FAILED'
            ? html`<div><${Button} title=${t('automations.runs.retry')} icon="IconRefresh" variant="secondary" size="small" isLoading=${isRetrying} onClick=${retry} /></div>`
            : null}
          ${isAdmin && STOPPABLE_RUN_STATUSES.has(run.status)
            ? html`<div><${Button} title=${t('automations.runs.stop')} icon="IconPlayerStop" variant="secondary" accent="danger" size="small" onClick=${() => setIsConfirmingStop(true)} /></div>`
            : null}
        </div>`
      : null}
    ${isConfirmingStop
      ? html`<${Modal} title=${t('automations.runs.stopTitle')} subtitle=${t('automations.runs.stopSubtitle')} confirmLabel=${t('automations.runs.stop')}
          confirmAccent="danger" onClose=${() => setIsConfirmingStop(false)} onConfirm=${stop} />`
      : null}
  </div>`;
}

/**
 * @param {{ automationId?: string | null, isAdmin: boolean, refreshKey?: number }} props
 */
export function AutomationRunList({ automationId = null, isAdmin, refreshKey = 0 }) {
  const [statusFilter, setStatusFilter] = useState(/** @type {'ALL' | 'FAILED' | 'WAITING'} */ ('ALL'));
  const status = statusFilter === 'ALL' ? null : statusFilter;
  const [state, setState] = useState(/** @type {{ runs: AutomationRun[], hasNextPage: boolean, endCursor: string | null } | null} */ (null));
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let isCancelled = false;
    setState(null);
    listAutomationRuns({ automationId, status, limit: PAGE_SIZE })
      .then(({ runs, pageInfo }) => {
        if (!isCancelled) setState({ runs, hasNextPage: pageInfo.hasNextPage, endCursor: pageInfo.endCursor });
      })
      .catch((error) => {
        if (isCancelled) return;
        showErrorSnackbar(error);
        setState({ runs: [], hasNextPage: false, endCursor: null });
      });
    return () => {
      isCancelled = true;
    };
  }, [automationId, statusFilter, reloadKey, refreshKey]);

  const loadMore = async () => {
    if (!state?.endCursor) return;
    try {
      const { runs, pageInfo } = await listAutomationRuns({ automationId, status, limit: PAGE_SIZE, startingAfter: state.endCursor });
      setState({ runs: [...state.runs, ...runs], hasNextPage: pageInfo.hasNextPage, endCursor: pageInfo.endCursor });
    } catch (error) {
      showErrorSnackbar(error);
    }
  };

  return html`<div class="automation-runs">
    <div class="automation-runs-toolbar">
      <${SegmentedControl} value=${statusFilter} onChange=${(value) => setStatusFilter(/** @type {'ALL' | 'FAILED' | 'WAITING'} */ (value))}
        options=${[
          { value: 'ALL', label: t('automations.runs.all') }, { value: 'FAILED', label: t('automations.runs.failedOnly'), icon: 'IconAlertTriangle' },
          { value: 'WAITING', label: t('automations.runs.waitingOnly'), icon: 'IconPlayerPause' },
        ]} />
      <${Button} title=${t('automations.runs.refresh')} icon="IconRefresh" variant="tertiary" size="small" onClick=${() => setReloadKey(reloadKey + 1)} />
    </div>
    ${state === null
      ? html`<${Skeleton} height=${40} radius=${8} /><${Skeleton} height=${40} radius=${8} />`
      : state.runs.length === 0
        ? html`<p class="automation-runs-empty">${statusFilter === 'FAILED' ? t('automations.runs.emptyFailed') : statusFilter === 'WAITING' ? t('automations.runs.emptyWaiting') : t('automations.runs.empty')}</p>`
        : html`<div class="automation-run-list">
            ${state.runs.map((run) => html`<${RunRow} key=${run.id} run=${run} showAutomationName=${!automationId} isAdmin=${isAdmin} onRetried=${() => setReloadKey(reloadKey + 1)} />`)}
          </div>`}
    ${state?.hasNextPage ? html`<div><${Button} title=${t('automations.runs.loadMore')} variant="secondary" size="small" onClick=${loadMore} /></div>` : null}
  </div>`;
}
